/**
 * DOM sidebar for Quadratic Gallery (二次函数图鉴): problem tab bar (one
 * button per exam problem), statement, givens badges, the 小问 scene tab
 * bar with its caption (switching scenes repaints the board) and the
 * collapsed solution card with the final answer rendered large. Switching
 * problems rebuilds the whole column.
 */

import type { ProblemDef } from "./problems/types";

/** Callbacks from the sidebar into the host (board / problem loader). */
export interface PanelHandlers {
  /** Scene tab bar asks to switch 小问 scenes. */
  onSelectScene(index: number): void;
  /** Problem tab bar asks to switch problems. */
  onSelectProblem(id: string): void;
}

export class QuadPanel {
  private readonly handlers: PanelHandlers;
  private problem: ProblemDef;
  private sceneIndex = 0;
  private sceneButtons: HTMLButtonElement[] = [];
  private captionEl: HTMLParagraphElement | null = null;
  private solutionBody: HTMLDivElement | null = null;
  private solutionToggle: HTMLButtonElement | null = null;

  /** Content below the problem tab bar, rebuilt on every problem switch. */
  private readonly body: HTMLDivElement;
  private readonly tabButtons = new Map<string, HTMLButtonElement>();

  constructor(host: HTMLElement, problems: readonly ProblemDef[], active: ProblemDef, handlers: PanelHandlers) {
    this.handlers = handlers;
    this.problem = active;

    host.replaceChildren();

    const tabs = document.createElement("nav");
    tabs.className = "q-tabs";
    for (const problem of problems) {
      const tab = document.createElement("button");
      tab.className = "q-tab";
      tab.type = "button";
      tab.textContent = problem.title;
      tab.addEventListener("click", () => {
        if (problem.id !== this.problem.id) {
          this.handlers.onSelectProblem(problem.id);
        }
      });
      this.tabButtons.set(problem.id, tab);
      tabs.appendChild(tab);
    }
    this.body = document.createElement("div");
    host.append(tabs, this.body);
    this.renderProblem();
  }

  /** Rebuild the whole column for a new problem and show its first scene. */
  showProblem(problem: ProblemDef): void {
    this.problem = problem;
    this.sceneIndex = 0;
    this.renderProblem();
  }

  /** Switch the active 小问 scene; also drives the QA hook. */
  showScene(index: number): void {
    if (index < 0 || index >= this.problem.scenes.length) {
      return;
    }
    this.sceneIndex = index;
    for (const [i, button] of this.sceneButtons.entries()) {
      button.classList.toggle("active", i === index);
    }
    const scene = this.problem.scenes[index];
    if (this.captionEl !== null && scene !== undefined) {
      this.captionEl.textContent = scene.caption;
    }
    this.handlers.onSelectScene(index);
  }

  /** Open the solution card (used by the QA hook). */
  revealAnswer(): void {
    this.setSolutionOpen(true);
  }

  /* ------------------------------------------------------------ building */

  /** Paint the statement / givens / scene tabs / solution of the active problem. */
  private renderProblem(): void {
    const problem = this.problem;
    this.sceneButtons = [];
    this.captionEl = null;
    this.solutionBody = null;
    this.solutionToggle = null;
    this.body.replaceChildren();

    for (const [id, tab] of this.tabButtons) {
      tab.classList.toggle("active", id === problem.id);
    }

    const title = document.createElement("h2");
    title.className = "q-title";
    title.textContent = problem.title;
    this.body.appendChild(title);

    const statement = document.createElement("p");
    statement.className = "q-statement";
    statement.textContent = problem.statement;
    this.body.appendChild(statement);

    const badges = document.createElement("div");
    badges.className = "q-badges";
    for (const given of problem.givens) {
      const badge = document.createElement("span");
      badge.className = "q-badge";
      badge.textContent = given.value === "" ? given.label : `${given.label} = ${given.value}`;
      badges.appendChild(badge);
    }
    this.body.appendChild(badges);

    const scenesHeading = document.createElement("h3");
    scenesHeading.className = "q-heading";
    scenesHeading.textContent = "小问场景";
    this.body.appendChild(scenesHeading);

    const scenes = document.createElement("div");
    scenes.className = "q-scenes";
    problem.scenes.forEach((scene, index) => {
      const button = document.createElement("button");
      button.className = "q-scene";
      button.type = "button";
      button.textContent = scene.tab;
      button.classList.toggle("active", index === this.sceneIndex);
      button.addEventListener("click", () => {
        this.showScene(index);
      });
      this.sceneButtons.push(button);
      scenes.appendChild(button);
    });
    this.body.appendChild(scenes);

    const caption = document.createElement("p");
    caption.className = "q-caption";
    caption.textContent = problem.scenes[this.sceneIndex]?.caption ?? "";
    this.captionEl = caption;
    this.body.appendChild(caption);

    const card = document.createElement("div");
    card.className = "q-solution";
    this.solutionToggle = document.createElement("button");
    this.solutionToggle.className = "q-solution-toggle";
    this.solutionToggle.type = "button";
    this.solutionToggle.textContent = "显示解答";
    this.solutionToggle.addEventListener("click", () => {
      const body = this.solutionBody;
      if (body !== null) {
        this.setSolutionOpen(body.hidden);
      }
    });
    this.solutionBody = document.createElement("div");
    this.solutionBody.className = "q-solution-body";
    this.solutionBody.hidden = true;
    const list = document.createElement("ol");
    list.className = "q-solution-steps";
    for (const paragraph of problem.solution) {
      const item = document.createElement("li");
      item.textContent = paragraph;
      list.appendChild(item);
    }
    const answer = document.createElement("div");
    answer.className = "q-answer";
    answer.textContent = problem.answer;
    this.solutionBody.append(list, answer);
    card.append(this.solutionToggle, this.solutionBody);
    this.body.appendChild(card);
  }

  private setSolutionOpen(open: boolean): void {
    if (this.solutionBody !== null) {
      this.solutionBody.hidden = !open;
    }
    if (this.solutionToggle !== null) {
      this.solutionToggle.textContent = open ? "收起解答" : "显示解答";
      this.solutionToggle.classList.toggle("open", open);
    }
  }
}
